import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-glow shadow-panel hover:shadow-elevated",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-panel",
        outline: "border-2 border-panel-border bg-background text-foreground hover:bg-hover hover:border-accent/50 shadow-panel",
        secondary: "bg-secondary text-secondary-foreground hover:bg-hover shadow-panel",
        ghost: "text-foreground hover:bg-hover hover:text-accent",
        link: "text-accent underline-offset-4 hover:underline hover:text-accent-glow",
        // Manga-specific variants
        manga: "bg-gradient-manga text-primary-foreground hover:shadow-elevated border-2 border-panel-border",
        accent: "bg-gradient-accent text-accent-foreground hover:bg-accent-glow shadow-elevated",
        panel: "bg-gradient-panel border-2 border-panel-border text-foreground hover:bg-hover shadow-panel",
        hero: "bg-accent text-accent-foreground hover:bg-accent-glow shadow-elevated border-2 border-accent/30 text-lg font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
